/* Program Name: SessionContext.js
 **  Description: a component that handles the state of the session for the
 **               entire application
 **
 ** Project    Date        Who  Comments
 ** ---------  ----------  ---  ---------------------------------------------------
 ** IS0200241  2021-04-12  STT  Initial program
 *********************************************************************************/

/**React hooks imports */
import React, {
  useContext,
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react'

/**Cookies import */
//DOCS: https://github.com/js-cookie/js-cookie
import Cookies from 'js-cookie'

/**import the ajax calls from SessionAPI */
//import { getMeta } from "./SessionAPI";

/**React Router Imports */
import { useHistory } from 'react-router-dom'
import { logout } from '../API/generalAPI'
import { useLoading } from './LoadingContext'

/**setSessionCookie */
/**removes the current session cookie and replaces it with the newly provided session */
export const getSessionCookie = () => {
  const sessionCookie = Cookies.get('Sess')
  if (sessionCookie === undefined) {
    return false
  } else {
    return true
  }
}

/**creating a context to hold the user authentication info */
//DOCS: https://reactjs.org/docs/context.html
const SessionContext = createContext({ user: { dn: 'empty' } })

/**creating a hook which will allow us to use this context */
export function useSession() {
  return useContext(SessionContext)
}

/**creating a provider to wrap around the app to provide access to the user to all children */
export default function SessionProvider({ children }) {
  /**holds a the history of the react-router */
  //DOCS: https://reactrouter.com/web/api/Hooks
  const history = useHistory()

  const loading = useRef(useLoading())

  /**user object */
  const [currentSession, setCurrentSession] = useState(null)

  const [warningWindow, setWarningWindow] = useState(false)

  const [countDown, setCountDown] = useState(25)

  let warningTimer

  const setWarningTimer = (duration) => {
    warningTimer = setTimeout(openWindow, duration)
  }

  const closeWindow = () => {
    setWarningWindow(false)
  }

  const openWindow = () => {
    setCountDown(25)
    setWarningWindow(true)
  }

  /**getSessionCookie */
  /**uses the Cookies package to get and serialize the session cookie and return it as a JSON object */
  const getMetaData = /* async */ () => {
    /* let response = await getMeta()
    setCurrentSession({
      user:response.data.ttMyRes[0], 
      currentStore:{
        storeAddress:response.data.ttMyRes[0].cCurrentStoreAddr, 
        storeNumber:response.data.ttMyRes[0].cCurrentStoreNumber
      }, 
      domain:'culvers' })
    return await response */
    setCurrentSession({
      user: { cFullName: 'CoreyTest' },
      currentStore: {
        storeAddress: 'address',
        storeNumber: 'number',
      },
      domain: 'culvers',
    })
  }

  /**login function that will take the users creditials and make an ajax call then either save the session to cookies or reject the login */
  const login = () => {
    Cookies.set('Sess', 'active')
    getMetaData()
    // .then(()=>{
    //   setWarningTimer((1000*60*60*24)-(1000*25))
    //   history.push("/");
    // }
    setWarningTimer(1000 * 60 * 60 * 24 - 1000 * 25)
    history.push('/')
  }

  const refreshSession = () => {
    window.clearTimeout(warningTimer)
    setWarningTimer(1000 * 30 - 1000 * 25)
  }

  /**lgout function that will send the current session token to the server in order kill the session and logout the user */
  const handleLogout = useCallback(() => {
    loading.current.startLoading()
    logout()
      .then(() => {})
      .catch((error) => {})
      .then(() => {
        Cookies.remove('Sess')
        closeWindow()
        setCurrentSession(null)
        history.push('/login')
        loading.current.stopLoading()
      })
  }, [history])

  /**values that will be avalible through the provider */
  const value = {
    currentSession,
    setCurrentSession,
    login,
    handleLogout,
    warningWindow,
    closeWindow,
    countDown,
    refreshSession,
    getMetaData,
  }

  useEffect(() => {
    if (warningWindow) {
      if (countDown > 0) {
        setTimeout(() => {
          setCountDown(countDown - 1)
        }, 1000)
      } else if (countDown === 0) {
        logout()
      }
    }
  }, [warningWindow, countDown])

  /**on app render the useEffect method will check if there is a session in the cookies and it will set the currentSession accordingly */
  useEffect(() => {}, [])

  /**rendering method */
  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  )
}
